import { Children, isValidElement, ReactNode } from "react";
import { IconTable } from "../../icons/IconTable/IconTable";
import { LeafIconButton } from "../../leaf/LeafIconButton/LeafIconButton";
import LeafRectangleLoader from "../../leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { BaseTile } from "../BaseTile/BaseTile";
import NoDataValue from "../NoDataValue/NoDataValue";
import "./PerformanceTrackingTile.scss";
import MetIconTitle from "../MetIconTitle/MetIconTitle";

export type TileSection = {
  title: ReactNode;
  value: ReactNode;
  className?: string;
  showIcon?: boolean;
  iconAriaLabel?: string;
  handleIconTableClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleBaselineIconTableClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
};

interface PerformanceTrackingTileProps {
  met: boolean;
  title: string;
  titleContentKey?: string;
  sections: TileSection[];
  isLoading?: boolean;
  showMetStatus?: boolean;
  children?: ReactNode;
}

const Footnote = ({ children }: { children: ReactNode }) => <div>{children}</div>;

const SkeletonLoader = () => {
  return (
    <div className="PerformanceTrackingTile">
      <BaseTile>
        <div className="PerformanceTrackingTile__tileHeader">
          <LeafRectangleLoader height="1.5rem" width="1.5rem" margin="0 0 2.75rem 0" isLoading={true} />
          <LeafRectangleLoader height="1.75rem" width="80%" margin="0 0 2.75rem 0" isLoading={true} />
        </div>
        <div className="PerformanceTrackingTile__content">
          <section className="PerformanceTrackingTile__block PerformanceTrackingTile__block--loader">
            <LeafRectangleLoader height="1rem" width="10rem" margin="0" isLoading={true} />
            <LeafRectangleLoader height="1.5rem" width="5rem" margin=".5rem 0 0 0" isLoading={true} />
          </section>

          <section className="PerformanceTrackingTile__block PerformanceTrackingTile__block--loader">
            <LeafRectangleLoader height="1rem" width="10rem" margin="0" isLoading={true} />
            <LeafRectangleLoader height="1.5rem" width="14rem" margin=".5rem 0 0 0" isLoading={true} />
          </section>

          <section className="PerformanceTrackingTile__block PerformanceTrackingTile__block--loader">
            <LeafRectangleLoader height="1rem" width="10rem" margin="0" isLoading={true} />
            <LeafRectangleLoader height="1.5rem" width="5rem" margin=".5rem 0 0 0" isLoading={true} />
          </section>
        </div>
      </BaseTile>
    </div>
  );
};

export const PerformanceTrackingTile = ({
  met,
  title,
  sections,
  isLoading = false,
  showMetStatus = true,
  children,
}: PerformanceTrackingTileProps) => {
  const footnote = Children.toArray(children).filter((child) => isValidElement(child) && child.type === Footnote);
  return isLoading ? (
    <SkeletonLoader />
  ) : (
    <div className="PerformanceTrackingTile">
      <BaseTile>
        <MetIconTitle showMetStatus={showMetStatus} met={met} title={title} />
        <div className="PerformanceTrackingTile__content">
          {sections.map(
            (
              {
                title,
                value,
                className,
                showIcon,
                iconAriaLabel,
                handleIconTableClick,
                handleBaselineIconTableClick,
                ariaLabel,
              },
              idx,
            ) => (
              <section
                key={idx}
                className={`PerformanceTrackingTile__block PerformanceTrackingTile__trackingBlock--${className || ""} `}
              >
                <div className="PerformanceTrackingTile__blockContent">
                  <span className="PerformanceTrackingTile__block--title">{title}</span>
                  <span className="PerformanceTrackingTile__block--value">
                    {ariaLabel ? <NoDataValue label={ariaLabel} /> : value}
                  </span>
                </div>
                {showIcon && (
                  <div>
                    <LeafIconButton
                      Icon={<IconTable />}
                      className="PerformanceTrackingTile__block--icon"
                      onClick={handleBaselineIconTableClick ?? handleIconTableClick}
                      id={`mpdIconTableButton-${idx}`}
                      aria-label={iconAriaLabel ?? "View table"}
                    />
                  </div>
                )}
              </section>
            ),
          )}
        </div>
        {footnote}
      </BaseTile>
    </div>
  );
};

PerformanceTrackingTile.Footnote = Footnote;

export default PerformanceTrackingTile;
